import React, { useMemo, useRef, useState } from 'react'
import styles from './InvoiceUploadSection.module.scss'
import axios from 'axios'
import { Button, Alert } from '@surecompanies/core_components'
import { Select, notification, Tooltip } from 'antd'
import {
  CheckCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  uploadInvoiceFile,
  UPLOAD_TIMEOUT_MS,
} from '../../api/invoice-delivery.api'
import { ParseWarning } from '../../api/dto/invoice-delivery.dto'
import {
  ReviewFile,
  UploadChoice,
  UploadReviewModal,
} from './emailTemplates/UploadReviewModal'
import { getDefaultInvoiceTemplate } from './emailTemplates/invoiceTemplates'
import {
  HISTORY_QUERY_KEY,
  INVOICE_DELIVERY_QUERY_KEY,
  STATS_QUERY_KEY,
} from '../../api/invoice-delivery-api-client'
import { SectionTitle } from './BulkInvoiceUpload.styles'
import {
  ClearAllButton,
  DropZone,
  DropZoneHint,
  DropZoneIcon,
  DropZoneText,
  FileItem,
  FileItemError,
  FileItemName,
  FileItemSize,
  FileItemStatus,
  FileList,
  FileListHeader,
  PeriodLabel,
  PeriodPickerRow,
  RemoveFileButton,
  UploadCard,
  UploadCardBody,
  UploadCardHeader,
  UploadCardToggle,
} from './InvoiceUploadSection.styles'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

const ACCEPTED_EXTENSIONS = '.xlsx'
const MAX_FILES = 150
const UPLOAD_CONCURRENCY = 5
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'failed'

interface FileItem {
  file: File
  status: FileUploadStatus
  failureReason?: string
  parseWarnings?: ParseWarning[] | null
}

interface NetworkResponseError extends Error {
  response?: { status: number }
}

const getUploadFailureReason = (err: NetworkResponseError): string => {
  if (axios.isCancel(err)) return 'Upload timed out'

  if (err.response) {
    return `Upload failed with status ${err.response.status}`
  }

  return err.message || 'Upload failed'
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface InvoiceUploadSectionProps {
  billingMonth: number
  billingYear: number
  onBillingMonthChange: (month: number) => void
  onBillingYearChange: (year: number) => void
  onUploadComplete: () => void
}

const InvoiceUploadSection: React.FC<InvoiceUploadSectionProps> = ({
  billingMonth,
  billingYear,
  onBillingMonthChange,
  onBillingYearChange,
  onUploadComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const [isExpanded, setIsExpanded] = useState(false)
  const [fileItems, setFileItems] = useState<FileItem[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{
    completed: number
    total: number
  } | null>(null)
  const [uploadSummary, setUploadSummary] = useState<{
    uploaded: number
    failed: number
    /** Files uploaded with at least one parser warning. */
    withWarnings: number
  } | null>(null)

  const defaultTemplate = getDefaultInvoiceTemplate(billingMonth)

  const [isReviewOpen, setIsReviewOpen] = useState(false)

  const isUploading = uploadProgress !== null
  const pendingCount = fileItems.filter((f) => f.status === 'pending').length

  const addFiles = (incoming: FileList | File[]) => {
    const xlsxFiles = Array.from(incoming).filter((f) =>
      f.name.endsWith('.xlsx')
    )
    const oversized = xlsxFiles.filter((f) => f.size > MAX_FILE_SIZE_BYTES)
    if (oversized.length > 0) {
      notification.error({
        message: 'File Too Large',
        description: `${oversized.map((f) => f.name).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 10 MB limit.`,
      })
    }
    const newFiles = xlsxFiles.filter((f) => f.size <= MAX_FILE_SIZE_BYTES)
    setFileItems((curr) => {
      const newItems: FileItem[] = newFiles.map((file) => ({
        file,
        status: 'pending',
      }))
      const combined = [...curr, ...newItems]
      if (combined.length > MAX_FILES) {
        notification.warning({
          message: 'File Limit',
          description: `Maximum ${MAX_FILES} files allowed. Some files were not added.`,
        })
        return combined.slice(0, MAX_FILES)
      }
      return combined
    })
  }

  const handleRemoveFile = (index: number) => {
    setFileItems((curr) => curr.filter((_, i) => i !== index))
  }

  const handleClearAll = () => {
    setFileItems([])
    setUploadSummary(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
    }
    e.target.value = ''
  }

  const billingPeriodStart = `${billingYear}-${String(billingMonth).padStart(2, '0')}-01`
  const pendingIndices = fileItems
    .map((item, idx) => (item.status === 'pending' ? idx : -1))
    .filter((idx) => idx >= 0)
  const reviewFiles = useMemo<ReviewFile[]>(
    () =>
      fileItems.flatMap((item, idx) =>
        item.status === 'pending' ? [{ key: String(idx), file: item.file }] : []
      ),
    [fileItems]
  )

  // Every upload gets a review step, so each file's template, version outcome
  // and email can be checked before anything goes up.
  const handleUploadClick = () => {
    if (pendingIndices.length > 0) setIsReviewOpen(true)
  }

  // Files removed in the review are left out of the choices, so they are
  // skipped here and cleared from the list with the rest once the upload ends.
  const handleReviewConfirm = (choices: Record<string, UploadChoice>) => {
    setIsReviewOpen(false)
    void handleUpload(
      choices,
      pendingIndices.filter((idx) => String(idx) in choices)
    )
  }

  const handleUpload = async (
    choices: Record<string, UploadChoice>,
    uploadIndices: number[]
  ) => {
    if (uploadIndices.length === 0) return

    const total = uploadIndices.length
    let uploadedCount = 0
    let failedCount = 0
    let withWarningsCount = 0

    setUploadSummary(null)
    setUploadProgress({ completed: 0, total })

    const queue = [...uploadIndices]

    const uploadOneFile = async (fileIdx: number) => {
      const file = fileItems[fileIdx].file
      setFileItems((prev) =>
        prev.map((item, idx) =>
          idx === fileIdx ? { ...item, status: 'uploading' } : item
        )
      )
      try {
        const result = await uploadInvoiceFile(
          file,
          { billingPeriodStart, ...choices[String(fileIdx)] },
          UPLOAD_TIMEOUT_MS
        )
        if (result.result === 'uploaded') {
          uploadedCount++
          const parseWarnings = result.delivery?.parseWarnings ?? null
          if (parseWarnings && parseWarnings.length > 0) {
            withWarningsCount++
          }
          setFileItems((prev) =>
            prev.map((item, idx) =>
              idx === fileIdx
                ? { ...item, status: 'success', parseWarnings }
                : item
            )
          )
        } else {
          failedCount++
          setFileItems((prev) =>
            prev.map((item, idx) =>
              idx === fileIdx
                ? { ...item, status: 'failed', failureReason: result.reason }
                : item
            )
          )
        }
      } catch (err) {
        failedCount++
        const reason = getUploadFailureReason(err as NetworkResponseError)
        setFileItems((prev) =>
          prev.map((item, idx) =>
            idx === fileIdx
              ? { ...item, status: 'failed', failureReason: reason }
              : item
          )
        )
      }
      setUploadProgress(
        (prev) => prev && { ...prev, completed: prev.completed + 1 }
      )
    }

    const worker = async () => {
      let fileIdx = queue.shift()
      while (fileIdx !== undefined) {
        await uploadOneFile(fileIdx)
        fileIdx = queue.shift()
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, total) }, worker)
    )

    setUploadProgress(null)
    setFileItems((prev) => prev.filter((item) => item.status === 'failed'))
    setUploadSummary({
      uploaded: uploadedCount,
      failed: failedCount,
      withWarnings: withWarningsCount,
    })
    void queryClient.invalidateQueries({
      queryKey: [INVOICE_DELIVERY_QUERY_KEY],
    })
    void queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
    void queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
    onUploadComplete()
  }

  const progressPct = uploadProgress
    ? Math.round((uploadProgress.completed / uploadProgress.total) * 100)
    : 0

  const uploadButtonLabel = isUploading
    ? `Uploading... ${progressPct}%`
    : `Upload ${pendingCount} ${pendingCount === 1 ? 'File' : 'Files'}`

  return (
    <UploadCard>
      <UploadCardHeader aria-expanded={isExpanded}>
        <SectionTitle>Upload invoice files</SectionTitle>
        <PeriodPickerRow>
          <PeriodLabel>Billing period</PeriodLabel>
          <Select
            value={billingMonth}
            onChange={onBillingMonthChange}
            className={styles.monthSelect}
            options={MONTHS.map((label, i) => ({ value: i + 1, label }))}
          />
          <Select
            value={billingYear}
            onChange={onBillingYearChange}
            className={styles.yearSelect}
            options={YEARS.map((y) => ({ value: y, label: String(y) }))}
          />
        </PeriodPickerRow>
        <UploadCardToggle
          $expanded={isExpanded}
          onClick={() => setIsExpanded((v) => !v)}
        >
          <DownOutlined />
        </UploadCardToggle>
      </UploadCardHeader>

      <UploadCardBody $expanded={isExpanded}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          multiple
          onChange={handleFileChange}
          className={styles.hiddenInput}
        />

        <DropZone
          $isDragOver={isDragOver}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Click or drag files to upload"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (!isUploading) fileInputRef.current?.click()
            }
          }}
        >
          <DropZoneIcon>
            <CloudUploadOutlined />
          </DropZoneIcon>
          <DropZoneText>Click to upload .xlsx files</DropZoneText>
          <DropZoneHint>
            .xlsx format only (max {MAX_FILES} files per upload)
          </DropZoneHint>
        </DropZone>

        {fileItems.length > 0 && (
          <>
            <FileListHeader>
              <span>
                {fileItems.length} {fileItems.length === 1 ? 'file' : 'files'}{' '}
                selected
              </span>
              {!isUploading && (
                <ClearAllButton onClick={handleClearAll}>
                  Clear all
                </ClearAllButton>
              )}
            </FileListHeader>
            <FileList>
              {fileItems.map((item, i) => (
                <FileItem key={`${item.file.name}-${i}`}>
                  <FileItemName>
                    <FileExcelOutlined className={styles.fileIcon} />
                    <span title={item.file.name}>{item.file.name}</span>
                  </FileItemName>
                  <FileItemSize>{formatFileSize(item.file.size)}</FileItemSize>
                  <FileItemStatus>
                    {item.status === 'uploading' && (
                      <>
                        <LoadingOutlined
                          spin
                          className={styles.uploadingIcon}
                        />{' '}
                        uploading
                      </>
                    )}
                    {item.status === 'success' && (
                      <>
                        <CheckCircleOutlined className={styles.successIcon} />{' '}
                        done
                        {item.parseWarnings?.map((w) => {
                          const prefix =
                            w.kind === 'missing' ? 'Missing' : 'Invalid'
                          const tooltip = w.cellAddress
                            ? `Cell ${w.cellAddress}: ${w.rawValue ? `"${w.rawValue}" — ` : ''}${w.reason}. Open the delivery from the table to fix.`
                            : `${w.reason}. Open the delivery from the table to fix.`
                          return (
                            <Tooltip
                              key={`${w.field}-${w.cellAddress}`}
                              title={tooltip}
                            >
                              <span
                                style={{
                                  color: '#92400e',
                                  marginLeft: 6,
                                  cursor: 'help',
                                }}
                              >
                                <WarningOutlined /> {prefix} {w.label}
                              </span>
                            </Tooltip>
                          )
                        })}
                      </>
                    )}
                    {item.status === 'failed' && (
                      <>
                        <ExclamationCircleOutlined
                          className={styles.failedIcon}
                        />{' '}
                        <span className={styles.failedText}>failed</span>
                        {!isUploading && (
                          <RemoveFileButton
                            onClick={() => handleRemoveFile(i)}
                            aria-label={`Remove ${item.file.name}`}
                          >
                            <CloseOutlined />
                          </RemoveFileButton>
                        )}
                      </>
                    )}
                    {item.status === 'pending' && !isUploading && (
                      <RemoveFileButton
                        onClick={() => handleRemoveFile(i)}
                        aria-label={`Remove ${item.file.name}`}
                      >
                        <CloseOutlined />
                      </RemoveFileButton>
                    )}
                    {item.status === 'pending' && isUploading && (
                      <span className={styles.pendingText}>—</span>
                    )}
                  </FileItemStatus>
                  {item.status === 'failed' && item.failureReason && (
                    <FileItemError>{item.failureReason}</FileItemError>
                  )}
                </FileItem>
              ))}
            </FileList>

            {(isUploading || pendingCount > 0) && (
              <Button
                type="primary"
                onClick={handleUploadClick}
                disabled={isUploading}
                className={
                  isUploading ? styles.uploadButtonProgress : undefined
                }
                style={
                  isUploading
                    ? ({
                        '--upload-progress-pct': `${progressPct}%`,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                {uploadButtonLabel}
              </Button>
            )}
          </>
        )}

        {uploadSummary !== null && (
          <Alert
            type={
              uploadSummary.failed === 0 && uploadSummary.withWarnings === 0
                ? 'success'
                : uploadSummary.uploaded === 0
                  ? 'error'
                  : 'warning'
            }
            message={(() => {
              const parts: string[] = []
              if (uploadSummary.uploaded > 0) {
                parts.push(`${uploadSummary.uploaded} uploaded`)
              }
              if (uploadSummary.failed > 0) {
                parts.push(`${uploadSummary.failed} failed`)
              }
              const summary = parts.length > 0 ? parts.join(', ') : 'No files'
              if (uploadSummary.withWarnings > 0) {
                return `${summary} — ${uploadSummary.withWarnings} ${uploadSummary.withWarnings === 1 ? 'invoice has' : 'invoices have'} unreadable cells. Open them from the deliveries table to fix Total Due before sending.`
              }
              return uploadSummary.failed === 0
                ? `All ${uploadSummary.uploaded} files uploaded successfully`
                : summary
            })()}
            showIcon
            closable
            onClose={() => setUploadSummary(null)}
          />
        )}
      </UploadCardBody>

      {isReviewOpen ? (
        <UploadReviewModal
          files={reviewFiles}
          defaultTemplate={defaultTemplate}
          billingPeriodStart={billingPeriodStart}
          billingPeriodLabel={`${MONTHS[billingMonth - 1]} ${billingYear}`}
          onCancel={() => setIsReviewOpen(false)}
          onConfirm={handleReviewConfirm}
        />
      ) : null}
    </UploadCard>
  )
}

export { InvoiceUploadSection }
